/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */



// global site stylesheet
import "./src/scss/app.scss"


// page transition timing
const transitionDelay = 400


// Fix scroll jump on page transition
export function shouldUpdateScroll({ routerProps: { location }, getSavedScrollPosition }) {
  if (location.action === 'PUSH') {
    window.setTimeout(() => window.scrollTo(0, 0), transitionDelay)
  } else {
    const savedPosition = getSavedScrollPosition(location)
    window.setTimeout(
      () => window.scrollTo(...(savedPosition || [0, 0])),
      transitionDelay
    )
  }
  return false
}


// fire on first page load
export function onInitialClientRender() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    if (params.has('edit')) {
      // Delay slightly to ensure DOM is settled
      setTimeout(() => {
        window.__injectSimpleEditor && window.__injectSimpleEditor();
      }, 0);
    }
  } catch { }
}


// fire on page change - delay for page transition so elements have changed in the DOM
export function onRouteUpdate({ location, prevLocation }) {
  setTimeout(function () {
    try {
      const params = new URLSearchParams(window.location.search || '');
      if (params.has('edit')) {
        window.__injectSimpleEditor && window.__injectSimpleEditor();
      }
    } catch { }
  }, transitionDelay + 100)
}


// Lightweight in-page editor injection when ?edit is present
if (typeof window !== 'undefined') {
  // Avoid double-inject
  let __editorInjected = false;

  const styleCss = `
  .__simpleEditor_bar{position:fixed;z-index:2147483647;right:16px;bottom:16px;left:auto;display:flex;gap:8px;align-items:stretch;background:#111;color:#fff;border-radius:10px;padding:10px 10px 10px 12px;box-shadow:0 6px 16px rgba(0,0,0,.3);font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;}
  .__simpleEditor_bar button{background:#2f80ed;border:none;color:#fff;padding:8px 12px;border-radius:8px;cursor:pointer;font-weight:600}
  .__simpleEditor_bar button[disabled]{opacity:.6;cursor:not-allowed}
  .__simpleEditor_bar .__secondary{background:#333}
  .__simpleEditor_bar .__note{min-width:240px;min-height:38px;border-radius:8px;border:1px solid #333;background:#1b1b1b;color:#fff;padding:8px;font-size:13px}
  .__simpleEditor_bar .__id{display:flex;gap:6px}
  .__simpleEditor_bar input{width:160px;border-radius:8px;border:1px solid #333;background:#1b1b1b;color:#fff;padding:8px;font-size:13px}
  .__simpleEditor_hint{position:fixed;top:8px;right:8px;background:#2f80ed;color:#fff;padding:6px 10px;border-radius:8px;font-size:12px;z-index:2147483647;}
  .__simpleEditor_hint a{color:#fff;text-decoration:underline}
  .__simpleEditor_editing [contenteditable="true"]{outline:2px dashed rgba(47,128,237,.75);outline-offset:2px}
  .__simpleEditor_bar .__format{display:flex;gap:6px;align-items:center;margin-right:8px}
  .__simpleEditor_bar .__format button{background:#222;padding:6px 9px;border-radius:6px;font-weight:700}
  /* Normalize list typography while editing to avoid span style injection on toggle */
  .__simpleEditor_editing ul, .__simpleEditor_editing ol, .__simpleEditor_editing li{font-size:inherit !important; line-height:inherit !important}
  `;

  const pickContentRoot = () => {
    return (
      document.querySelector('main[role="main"]') ||
      document.querySelector('main') ||
      document.querySelector('article') ||
      document.querySelector('#content, .content, [data-content-root]') ||
      document.body
    );
  };

  const selectorFor = (el, root) => {
    if (!el || el === root || el.nodeType !== 1) return '';
    if (el.id) return `#${CSS.escape(el.id)}`;
    const parts = [];
    let node = el;
    while (node && node !== root && node.nodeType === 1) {
      let sel = node.nodeName.toLowerCase();
      if (node.id) {
        sel += `#${CSS.escape(node.id)}`;
        parts.unshift(sel);
        break;
      }
      if (node.classList && node.classList.length) {
        sel += '.' + Array.from(node.classList).map(c => CSS.escape(c)).join('.');
      }
      const parent = node.parentElement;
      if (parent) {
        const siblings = Array.from(parent.children).filter(n => n.nodeName === node.nodeName);
        if (siblings.length > 1) {
          const idx = siblings.indexOf(node) + 1;
          sel += `:nth-of-type(${idx})`;
        }
      }
      parts.unshift(sel);
      node = parent;
    }
    return parts.join(' > ');
  };

  window.__injectSimpleEditor = () => {
    if (__editorInjected) return;
    __editorInjected = true;

    // Inject styles
    const style = document.createElement('style');
    style.textContent = styleCss;
    document.head.appendChild(style);

    // Prefer semantic tags over inline CSS where possible
    try { document.execCommand('styleWithCSS', false, false); } catch { }

    // html2canvas loader for screenshots
    const loadHtml2Canvas = () => new Promise((resolve, reject) => {
      if (window.html2canvas) return resolve(window.html2canvas);
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
      s.async = true;
      s.onload = () => resolve(window.html2canvas);
      s.onerror = reject;
      document.head.appendChild(s);
    });

    const captureFullPageScreenshot = async () => {
      try {
        const h2c = await loadHtml2Canvas();
        const doc = document.documentElement;
        const width = Math.max(doc.scrollWidth, doc.clientWidth);
        const height = Math.max(doc.scrollHeight, doc.clientHeight);
        const canvas = await h2c(document.body, {
          useCORS: true,
          allowTaint: false,
          backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
          windowWidth: width,
          windowHeight: height,
          scrollX: 0,
          scrollY: 0,
          scale: 1
        });
        const quality = 0.85;
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        const b64 = (dataUrl.split(',')[1] || '');
        const pathPart = (window.location.pathname || '/').replace(/\/+/, '/').replace(/[^a-z0-9/_-]+/gi, '-').replace(/^-+|-+$/g, '');
        const ts = new Date().toISOString().replace(/[:.]/g, '-');
        return {
          filename: `page-${pathPart || 'root'}-${ts}.jpg`,
          contentType: 'image/jpeg',
          data: b64
        };
      } catch (e) {
        return null;
      }
    };

    const contentRoot = pickContentRoot();
    const originalHTML = contentRoot.innerHTML;
    const changedSelectors = new Set();
    let changed = false;
    const baselineByElement = new WeakMap();
    const changesBySelector = new Map();
    const baselineByBlock = new WeakMap();
    const blockChangesBySelector = new Map();

    // Make editable within content root
    const textOf = (el) => (el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : '');
    const editableSelectors = "h1, .heroContent__wrapper, .tripleArticle__article, .article__wrapper, .copyBlock__content, section.subHero__panel.light > div > div > div:nth-child(2), .quotePanel--copy, .quotePanel--author, .card__title, .card__link, .card__copy, .pricingPanel__tagLine, .pricingPanel__copy, .pricingPanel__title, .pricingPanel__desc, .faq__question, .faq__copy, .article__wrapper, .hero .sub-heading, .heroBottom .medium-6, .heroBottom .medium-5, .heroBottom .medium-10, .offers__heading, .offers__heading h4, .benefitPanel .medium-10, .benefitPanel h2, .accordion__title, .accordion__blurb, .card--form div";
    const BLOCK_ROOT_SELECTOR = editableSelectors;
    const makeEditable = (root) => {
      // Enable on common text elements
      const editableNodes = root.querySelectorAll(editableSelectors);
      editableNodes.forEach(n => {
        n.setAttribute('contenteditable', 'true');
        if (!baselineByElement.has(n)) baselineByElement.set(n, textOf(n));
        if (!baselineByBlock.has(n)) baselineByBlock.set(n, n.innerHTML);
      });
      root.classList.add('__simpleEditor_editing');
    };
    makeEditable(contentRoot);

    const recordChange = (el) => {
      if (!el || !(el instanceof Element)) return;
      // find nearest editable block ancestor
      const block = el.closest(BLOCK_ROOT_SELECTOR) || el;
      const sel = selectorFor(block, contentRoot);
      if (!sel) return;
      // text baseline (keep for keyboard shortcuts UX)
      if (!baselineByElement.has(block)) baselineByElement.set(block, textOf(block));
      const oldText = baselineByElement.get(block) || '';
      const newText = textOf(block);
      if (oldText === newText) {
        changesBySelector.delete(sel);
      } else {
        const entry = changesBySelector.get(sel) || { selector: sel, oldText, newText };
        entry.newText = newText;
        changesBySelector.set(sel, entry);
      }
      // html baseline for block-level summary
      if (!baselineByBlock.has(block)) baselineByBlock.set(block, block.innerHTML);
      const oldHtml = baselineByBlock.get(block) || '';
      const newHtml = block.innerHTML;
      if (oldHtml === newHtml) {
        blockChangesBySelector.delete(sel);
      } else {
        blockChangesBySelector.set(sel, { selector: sel, oldHtml, newHtml, _block: block });
      }
    };

    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        if (m.type === 'characterData') {
          const el = m.target.parentElement;
          changedSelectors.add(selectorFor(el, contentRoot));
          recordChange(el);
          changed = true;
        } else if (m.type === 'childList') {
          if (m.target && m.target.nodeType === 1) {
            // Ensure new nodes are editable and baseline captured
            m.addedNodes && m.addedNodes.forEach(node => {
              if (node && node.nodeType === 1) {
                const addedRoot = node;
                const nodes = [addedRoot, ...((addedRoot.querySelectorAll && Array.from(addedRoot.querySelectorAll(editableSelectors))) || [])];
                nodes.forEach(n => {
                  if (!(n instanceof Element)) return;
                  if (n.matches && n.matches(editableSelectors)) {
                    n.setAttribute('contenteditable', 'true');
                    if (!baselineByElement.has(n)) baselineByElement.set(n, textOf(n));
                  }
                });
              }
            });
            changedSelectors.add(selectorFor(m.target, contentRoot));
            recordChange(m.target);
            changed = true;
          }
        } else if (m.type === 'attributes') {
          changedSelectors.add(selectorFor(m.target, contentRoot));
          recordChange(m.target);
          changed = true;
        }
      }
    });
    observer.observe(contentRoot, { subtree: true, characterData: true, childList: true, attributes: false });

    // Simple formatting shortcuts (like nicedit basics)
    const onKeyDown = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const k = e.key.toLowerCase();
      if (k === 'b') {
        e.preventDefault(); document.execCommand('bold');
      } else if (k === 'i') {
        e.preventDefault(); document.execCommand('italic');
      } else if (k === 'u') {
        e.preventDefault(); document.execCommand('underline');
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    // Toolbar UI
    const bar = document.createElement('div');
    bar.className = '__simpleEditor_bar';
    bar.innerHTML = `
      <div class="__format" title="Formatting">
        <button type="button" class="__fmt __b" data-cmd="bold" aria-label="Bold"><b>B</b></button>
        <button type="button" class="__fmt __i" data-cmd="italic" aria-label="Italic"><i>I</i></button>
        <button type="button" class="__fmt __u" data-cmd="underline" aria-label="Underline"><u>U</u></button>
        <button type="button" class="__fmt __h1" data-cmd="formatBlock" data-value="H1" aria-label="Heading 1">H1</button>
        <button type="button" class="__fmt __h2" data-cmd="formatBlock" data-value="H2" aria-label="Heading 2">H2</button>
        <button type="button" class="__fmt __h3" data-cmd="formatBlock" data-value="H3" aria-label="Heading 3">H3</button>
        <button type="button" class="__fmt __h4" data-cmd="formatBlock" data-value="H4" aria-label="Heading 4">H4</button>
        <button type="button" class="__fmt __ul" data-cmd="insertUnorderedList" aria-label="Bulleted list">•</button>
        <button type="button" class="__fmt __ol" data-cmd="insertOrderedList" aria-label="Numbered list">1.</button>
        <button type="button" class="__fmt __link" data-action="link" aria-label="Insert link">🔗</button>
      </div>
      <textarea class="__note" placeholder="Notes for this change (optional)"></textarea>
      <div class="__id">
        <input class="__name" placeholder="Your name (optional)" />
        <input class="__email" type="email" placeholder="Email (optional)" />
      </div>
      <button class="__save">Save & Send</button>
      <button class="__cancel __secondary">Exit</button>
    `;
    document.body.appendChild(bar);

    const hint = document.createElement('div');
    hint.className = '__simpleEditor_hint';
    hint.textContent = 'Edit mode enabled. Click text to edit.';
    document.body.appendChild(hint);
    setTimeout(() => { hint.remove(); }, 3500);

    const btnSave = bar.querySelector('button.__save');
    const btnCancel = bar.querySelector('button.__cancel');
    const noteEl = bar.querySelector('textarea.__note');
    const nameEl = bar.querySelector('input.__name');
    const emailEl = bar.querySelector('input.__email');
    const fmtButtons = bar.querySelectorAll('button.__fmt[data-cmd], button.__fmt[data-action]');

    const getSel = () => window.getSelection && window.getSelection();
    const getBlockRoot = (node) => {
      try { return node && (node.nodeType === 1 ? node : node.parentElement)?.closest && (node.parentElement || node).closest(BLOCK_ROOT_SELECTOR); } catch { return null; }
    };
    const placeCaretAtEnd = (el) => {
      try {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = getSel();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch { }
    };
    const replaceBlockTag = (oldEl, newTagName) => {
      try {
        if (!oldEl || !newTagName) return;
        const newEl = document.createElement(newTagName.toLowerCase());
        // preserve classes/ids only when converting between headings/paragraph
        if (/^h[1-6]$|^p$/i.test(oldEl.tagName) && /^h[1-6]$|^p$/i.test(newTagName)) {
          const hasClasses = oldEl.classList && oldEl.classList.length > 0;
          if (hasClasses) newEl.className = oldEl.className;
          if (oldEl.id) newEl.id = oldEl.id;
        }
        while (oldEl.firstChild) newEl.appendChild(oldEl.firstChild);
        oldEl.parentNode.replaceChild(newEl, oldEl);
        if (newEl.getAttribute('class') === '') newEl.removeAttribute('class');
        placeCaretAtEnd(newEl);
        return newEl;
      } catch { }
    };
    const expandToAndToggleInline = (ancestorEl, cmd) => {
      try {
        const sel = getSel();
        if (!sel) return false;
        const range = document.createRange();
        range.selectNodeContents(ancestorEl);
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand(cmd);
        return true;
      } catch { return false; }
    };

    // cleanup helpers for browser-inserted spans
    const unwrapEl = (el) => {
      if (!el || !el.parentNode) return;
      while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
      el.parentNode.removeChild(el);
    };
    const cleanupFontSizeSpans = (root) => {
      try {
        if (!root) return;
        root.querySelectorAll('span[style]').forEach(s => {
          const st = s.style;
          if (!st) return;
          // unwrap spans that only carry font-size preservation
          if (st.length === 1 && st.fontSize) unwrapEl(s);
        });
      } catch { }
    };

    // Keep selection while clicking toolbar and support toggling within parent tag
    fmtButtons.forEach(btn => {
      btn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const action = btn.getAttribute('data-action');
        const cmd = btn.getAttribute('data-cmd');
        const val = btn.getAttribute('data-value');
        const sel = getSel();
        const anchorNode = sel && (sel.anchorNode || sel.focusNode);
        const currentBlock = getBlockRoot(anchorNode) || contentRoot;

        if (action === 'link') {
          try {
            if (!sel || sel.rangeCount === 0) return;
            const anchor = anchorNode && (anchorNode.parentElement ? anchorNode.parentElement.closest('a') : null);
            const existing = anchor && anchor.getAttribute('href') ? anchor.getAttribute('href') : '';
            let url = window.prompt('Enter link URL', existing);
            if (!url) return;
            if (sel.isCollapsed && anchor && currentBlock.contains(anchor)) {
              anchor.setAttribute('href', url);
            } else {
              document.execCommand('createLink', false, url);
            }
          } catch { }
          return;
        }

        // Inline formatting toggles on collapsed selection by unwrapping nearest matching ancestor
        if (cmd === 'bold' || cmd === 'italic' || cmd === 'underline') {
          const map = {
            bold: 'b,strong',
            italic: 'i,em',
            underline: 'u'
          };
          const ancestor = anchorNode && (anchorNode.parentElement ? anchorNode.parentElement.closest(map[cmd]) : null);
          if (sel && sel.isCollapsed && ancestor && currentBlock.contains(ancestor)) {
            if (expandToAndToggleInline(ancestor, cmd)) return;
          }
          try { document.execCommand(cmd); } catch { }
          return;
        }

        // Headings: toggle off if already inside same heading; otherwise set that heading
        if (cmd === 'formatBlock' && val) {
          const nearestHeading = anchorNode && (anchorNode.parentElement ? anchorNode.parentElement.closest('h1,h2,h3,h4,h5,h6') : null);
          const want = String(val).toUpperCase();
          if (nearestHeading && currentBlock.contains(nearestHeading)) {
            if (nearestHeading.tagName.toUpperCase() === want) {
              // toggle off -> paragraph
              replaceBlockTag(nearestHeading, 'p');
              return;
            }
            // replace with requested heading tag
            replaceBlockTag(nearestHeading, want);
            return;
          }
          // No existing heading ancestor; apply via execCommand for convenience
          try {
            if (!document.execCommand(cmd, false, val)) {
              document.execCommand(cmd, false, `<${val}>`);
            }
          } catch { }
          return;
        }

        // Lists and others fall back to execCommand
        try {
          document.execCommand(cmd);
          if (cmd === 'insertUnorderedList' || cmd === 'insertOrderedList') {
            // Cleanup stray font-size spans produced by toggling lists
            cleanupFontSizeSpans(currentBlock);
          }
        } catch { }
      });
    });

    const cleanUrl = () => {
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete('edit');
        return u.toString();
      } catch {
        return window.location.href;
      }
    };

    const setBusy = (busy) => {
      btnSave.disabled = !!busy;
      btnCancel.disabled = !!busy;
    };

    btnCancel.addEventListener('click', () => {
      // Restore original and exit
      observer.disconnect();
      contentRoot.innerHTML = originalHTML;
      bar.remove();
      document.querySelectorAll('[contenteditable="true"]').forEach(n => n.removeAttribute('contenteditable'));
      contentRoot.classList.remove('__simpleEditor_editing');
      document.removeEventListener('keydown', onKeyDown, true);
      __editorInjected = false;
    });

    btnSave.addEventListener('click', async () => {
      setBusy(true);
      try {
        const diffs = Array.from(blockChangesBySelector.values())
          .map(d => ({ selector: d.selector, oldHtml: String(d.oldHtml || ''), newHtml: String(d.newHtml || '') }));

        // Try to capture full-page screenshot (best-effort)
        const screenshot = await captureFullPageScreenshot().catch(() => null);

        const payload = {
          url: cleanUrl(),
          title: document.title,
          note: noteEl.value || '',
          diffs,
          ...(screenshot ? { screenshot } : {}),
          name: nameEl.value || '',
          email: emailEl.value || '',
          company: '' // honeypot
        };

        const res = await fetch('/.netlify/functions/jira-content-update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || j.response !== 'success') {
          throw new Error(j?.error?.message || 'Failed to send update');
        }
        btnSave.textContent = 'Saved ✓';
        setTimeout(() => { btnSave.textContent = 'Save & Send'; }, 2000);

        if (j && (j.issueUrl || j.issueKey)) {
          const done = document.createElement('div');
          done.className = '__simpleEditor_hint';
          const linkText = j.issueKey ? j.issueKey : 'View in Jira';
          const href = j.issueUrl || '';
          done.innerHTML = href
            ? `Jira issue <a href="${href}" target="_blank" rel="noopener">${linkText}</a> ${j.issueKey ? 'updated' : 'created'}.`
            : `Jira issue ${linkText} updated.`;
          document.body.appendChild(done);
          setTimeout(() => { try { done.remove(); } catch { } }, 6000);
        }
        // refresh baselines for changed blocks
        Array.from(blockChangesBySelector.values()).forEach(ch => {
          if (ch._block) {
            baselineByBlock.set(ch._block, ch._block.innerHTML);
            baselineByElement.set(ch._block, textOf(ch._block));
          }
        });
        changesBySelector.clear();
        blockChangesBySelector.clear();
      } catch (e) {
        alert('Error sending update: ' + (e.message || e));
      } finally {
        setBusy(false);
      }
    });
  };
}
